import React, { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { calculatePosition, getBestPlacement, getPopoverBorder, isElementInViewport } from './helpers';
import { PopoverArrow } from './PopOverArrow';
import { Placement, PopoverPosition } from './types';

// --- Styles and Constants ---
const popoverStyles = {
  position: 'absolute',
  background: '#fff',
  color: '#222',
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  padding: '12px 16px',
  borderRadius: '8px',
  zIndex: 9999,
  pointerEvents: 'auto',
} as const;

const arrowBorderColor = '#ccc';
const fadeDuration = 250;

const transformMap: Record<string, string> = {
  top: 'translate(-50%, -100%)',
  right: 'translate(0, -50%)',
  bottom: 'translate(-50%, 0)',
  left: 'translate(-100%, -50%)',
};

// --- Types ---

interface ComponentProps {
  /**
   * Indicates if the popover is currently open.
   * @default false
   * This prop controls the visibility of the popover.
   */
  isOpen: boolean;
  /**
   * The ID of the target element that the popover is anchored to.
   * This should match the ID of an element in the DOM.
   */
  target: string;
  /**
   * The placement of the popover relative to the target element.
   * @default 'top'
   * Options: 'top', 'right', 'bottom', 'left'.
   */
  placement?: Placement;
  /**
   * Function to toggle the visibility of the popover.
   * This is typically used to close the popover when clicked outside.
   */
  toggle?: () => void;
  /**
   * Optional styles to override the default popover styles.
   * This allows for customization of the popover's appearance.
   */
  styleOverrides?: CSSProperties;
  /**
   * The content to be displayed inside the popover.
   * This can be any valid React node, such as text, components, or HTML elements.
   */
  children: ReactNode;
  /**
   * Enables automatic flipping of the popover position based on available space.
   * @default false
   */
  autoFlip?: boolean;
  /**
   * Optional target element for the portal.
   * If provided, the popover will be rendered as a child of this element.
   */
  portalTarget?: HTMLElement;
  /**
   * ARIA label for the popover.
   * This is used for accessibility purposes.
   */
  ariaLabel?: string;
  /**
   * ARIA described by ID for the popover.
   * This is used for accessibility purposes to describe the popover content.
   */
  ariaDescribedBy?: string;
  /**
   * Hides the arrow pointer of the popover.
   * @default false
   */
  hideArrow?: boolean;
  /**
   * Optional className for the popover container.
   */
  className?: string;
  /**
   * Whether this popover is a modal dialog.
   * @default true
   *
   * `true` keeps the historical markup: `role="dialog" aria-modal="true"`, which tells assistive
   * technology the rest of the page is unavailable while it is open.
   *
   * Set `false` for a popover that opens on hover/focus and holds no focusable content. There,
   * `aria-modal` is actively harmful - focus stays on the TRIGGER (outside the "modal"), so a screen
   * reader collapses the user's view of the page to a container they can never enter and cannot Tab
   * past without blurring the trigger, which closes it. `role="tooltip"` is the correct role for
   * that shape; pair it with `aria-describedby` on the trigger so the content is actually announced.
   */
  isModal?: boolean;
}

/**
 *  PopOver component that displays content in a popover style.
 *  Handles positioning, visibility, and fade effects.
 * @param param0 - Props for the PopOver component.
 * @returns PopOver component that displays content in a popover style.
 * Handles positioning, visibility, and fade effects.
 */
const PopOver: React.FC<ComponentProps> = ({
  isOpen,
  target,
  placement = 'top',
  toggle,
  styleOverrides = {},
  children,
  autoFlip = false,
  portalTarget,
  ariaLabel,
  ariaDescribedBy,
  hideArrow = false,
  className = '',
  isModal = true,
}) => {
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const [visible, setVisible] = useState(true);
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [currentPlacement, setCurrentPlacement] = useState<Placement>(placement);
  const popoverRef = useRef<HTMLDivElement>(null);

  // --- Fade in/out logic ---
  useEffect(() => {
    let openTimeout: ReturnType<typeof setTimeout> | undefined;

    if (isOpen) {
      setShouldRender(true);
      openTimeout = setTimeout(() => setVisible(true), 10);
    } else {
      setVisible(false);
      const closeTimeout = setTimeout(() => setShouldRender(false), fadeDuration);
      return () => {
        if (openTimeout) clearTimeout(openTimeout);
        clearTimeout(closeTimeout);
      };
    }

    return () => {
      if (openTimeout) clearTimeout(openTimeout);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const bestPlacement = getBestPlacement(target, placement, autoFlip);
    setCurrentPlacement(bestPlacement);

    const anchor = document.getElementById(target);
    if (!anchor) return;

    setPosition(calculatePosition(target, bestPlacement));
    setVisible(isElementInViewport(anchor));

    const handleUpdate = () => {
      const anchorEl = document.getElementById(target);
      // Unmount if target is missing or not in viewport
      if (!anchorEl || !isElementInViewport(anchorEl)) {
        setVisible(false);
        setShouldRender(false);
        return;
      }
      const newPlacement = getBestPlacement(target, placement, autoFlip);
      setCurrentPlacement(newPlacement);
      setPosition(calculatePosition(target, newPlacement));
      setVisible(true);
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);

    const observer = new MutationObserver(handleUpdate);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });

    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
      observer.disconnect();
    };
  }, [isOpen, target, placement, autoFlip]);

  // --- Keyboard accessibility: close on Escape, focus trap ---
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && toggle) toggle();
      // Focus trap: keep focus inside popover
      if (popoverRef.current && e.key === 'Tab') {
        const focusable = popoverRef.current.querySelectorAll<HTMLElement>(
          'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])',
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        } else if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggle]);

  // Close popover when clicking outside
  useEffect(() => {
    if (!isOpen || !toggle) return;

    function handleClickAway(event: MouseEvent) {
      if (!popoverRef.current) return;
      if (!popoverRef.current.contains(event.target as Node)) {
        toggle?.();
      }
    }

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [isOpen, toggle]);

  if (!shouldRender || !position) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className={className}
      style={{
        ...popoverStyles,
        top: position.top,
        left: position.left,
        transform: transformMap[currentPlacement],
        ...styleOverrides,
        ...getPopoverBorder(currentPlacement, arrowBorderColor),
        opacity: visible ? 1 : 0,
        transition: `opacity ${fadeDuration}ms`,
        pointerEvents: visible ? 'auto' : 'none',
      }}
      tabIndex={-1}
      role={isModal ? 'dialog' : 'tooltip'}
      // Omitted entirely rather than set to "false" on the tooltip variant - `aria-modal="false"` is
      // the spec default and carries no meaning, so the attribute simply should not be there.
      aria-modal={isModal ? 'true' : undefined}
      // A `dialog` must carry an accessible name or axe reports aria-dialog-name. A `tooltip` must
      // NOT: its own text content is the name, and labelling it as well makes a screen reader
      // announce the label in place of the content the user opened it to read.
      aria-label={isModal ? ariaLabel : undefined}
      aria-describedby={ariaDescribedBy}
    >
      {!hideArrow && <PopoverArrow placement={currentPlacement} arrowBorderColor={arrowBorderColor} />}
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>,
    portalTarget || document.body,
  );
};

export default PopOver;
